import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchItemKitById, updateItemKit } from '../services/api.ts';
import { ArrowLeft, Upload, Image as ImageIcon, Loader } from 'lucide-react';
import './EditItemKit.css';

const EditItemKit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [formData, setFormData] = useState({
    name: '',
    avater: '',
    lowest_price: 0,
    is_feature: false,
    enable: true
  });

  useEffect(() => {
    const loadItemKit = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const data = await fetchItemKitById(id);
        setFormData({
          name: data.name || '',
          avater: data.avater || '',
          lowest_price: data.lowest_price || 0,
          is_feature: data.is_feature || false,
          enable: data.enable || true
        });
        setImagePreview(data.avater || '');
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load item kit');
        console.error('Error loading item kit:', err);
      } finally {
        setLoading(false);
      }
    };

    loadItemKit();
  }, [id]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value;
    setFormData({ ...formData, avater: url });
    setImagePreview(url);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    try {
      setError(null);
      await updateItemKit(id, formData);
      navigate('/dashboard/item-kits');
    } catch (err: any) {
      const errorMessage = err.response?.data?.message 
        || err.response?.data?.error 
        || err.message 
        || 'Failed to update item kit';
      setError(errorMessage);
      console.error('Update error:', err.response?.data);
    }
  };

  if (loading) {
    return (
      <div className="edit-item-kit">
        <div className="loading-container">
          <Loader className="loading-spinner-icon" size={40} />
          <p>Loading item kit...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="edit-item-kit">
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate('/dashboard/item-kits')}>
          <ArrowLeft size={20} />
          Back to Item Kits
        </button>
        <h2>Edit Item Kit</h2>
      </div>

      {error && (
        <div className="error-alert">
          <span>⚠️ {error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-grid">
          <div className="form-section">
            <h3 className="section-title">Basic Information</h3>
            
            <div className="form-group">
              <label>Item Kit Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter item kit name"
                required
              />
            </div>

            <div className="form-group">
              <label>Lowest Price *</label>
              <div className="input-with-icon">
                <span className="input-icon">$</span>
                <input
                  type="number"
                  step="0.01"
                  value={formData.lowest_price}
                  onChange={(e) => setFormData({ ...formData, lowest_price: parseFloat(e.target.value) })}
                  placeholder="0.00"
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label>Image URL *</label>
              <div className="input-with-icon">
                <Upload size={18} className="input-icon" />
                <input
                  type="text"
                  value={formData.avater}
                  onChange={handleImageChange}
                  placeholder="https://example.com/image.jpg"
                  required
                />
              </div>
              <p className="field-hint">Enter the URL of the item kit image</p>
            </div>

            <div className="form-switches">
              <div className="switch-group">
                <div className="switch-content">
                  <label className="switch-label">Featured Item Kit</label>
                  <p className="switch-description">Display this item kit in the featured section</p>
                </div>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={formData.is_feature}
                    onChange={(e) => setFormData({ ...formData, is_feature: e.target.checked })}
                  />
                  <span className="slider"></span>
                </label>
              </div>

              <div className="switch-group">
                <div className="switch-content">
                  <label className="switch-label">Active Status</label>
                  <p className="switch-description">Enable this item kit for public viewing</p>
                </div>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={formData.enable}
                    onChange={(e) => setFormData({ ...formData, enable: e.target.checked })}
                  />
                  <span className="slider"></span>
                </label>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3 className="section-title">Preview</h3>
            
            <div className="preview-card">
              {imagePreview ? (
                <img src={imagePreview} alt="Preview" className="preview-image" />
              ) : (
                <div className="preview-placeholder">
                  <ImageIcon size={48} />
                  <p>Image preview will appear here</p>
                </div>
              )}
              
              <div className="preview-details">
                <h4>{formData.name || 'Item Kit Name'}</h4>
                <div className="preview-price">${formData.lowest_price.toFixed(2)}</div>
                
                <div className="preview-badges">
                  {formData.is_feature && (
                    <span className="preview-badge featured">Featured</span>
                  )}
                  <span className={`preview-badge ${formData.enable ? 'active' : 'inactive'}`}>
                    {formData.enable ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>

            <div className="info-box">
              <h4>💡 Tips</h4>
              <ul>
                <li>Use high-quality images (min 800x600px)</li>
                <li>Keep names clear and descriptive</li>
                <li>Set competitive pricing</li>
                <li>Enable featured for popular items</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button 
            type="button" 
            className="btn-secondary"
            onClick={() => navigate('/dashboard/item-kits')}
          >
            Cancel
          </button>
          <button type="submit" className="btn-primary">
            Save Changes
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditItemKit;
